/**
 * ReviewStep — Step 5 of the Rodeo Wizard
 *
 * Summary of all configured data before publishing.
 */

import { Calendar, Clock, MapPin, DollarSign, Users, Layers, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useRodeoStore } from '@/state_management_and_stores/rodeo.store';
import { formatDate, formatTime } from '@/utils/formatters';

export function ReviewStep() {
  const basicInfo = useRodeoStore((s) => s.basicInfo);
  const performances = useRodeoStore((s) => s.performances);
  const events = useRodeoStore((s) => s.events);
  const draftId = useRodeoStore((s) => s.draftId);
  const isPublished = useRodeoStore((s) => s.isPublished);
  const setIsPublished = useRodeoStore((s) => s.setIsPublished);

  const perfName = (key: string) =>
    performances.find((p) => p._key === key)?.performance_name ?? 'Unassigned';

  const warnings: string[] = [];
  if (!basicInfo.name.trim()) warnings.push('Rodeo name is required');
  if (performances.length === 0) warnings.push('At least one performance is recommended');
  if (events.length === 0) warnings.push('At least one event is recommended');
  if (!draftId) warnings.push('Draft has not been saved yet — click "Save" first');

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Review & Publish</h2>
        <p className="text-sm text-muted-foreground">
          Review your rodeo configuration before saving.
        </p>
      </div>

      {/* Publish toggle — sets the intended publish state. The single
          "Save" button at the bottom of the wizard persists this along
          with everything else, so there's no separate "Save & Publish"
          action anymore. */}
      <Card>
        <CardContent className="flex items-start justify-between gap-4 py-4">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">Publish this rodeo</p>
              <Badge
                variant={isPublished ? 'default' : 'secondary'}
                className={`text-[10px] ${
                  isPublished
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                }`}
              >
                {isPublished ? 'Published' : 'Draft'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Published rodeos are visible to competitors and accept
              entries. Toggle off to move back to draft.
            </p>
          </div>
          <Switch checked={isPublished} onCheckedChange={setIsPublished} />
        </CardContent>
      </Card>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-1">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Heads up
          </p>
          <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i}>• {w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Basic Info summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="font-medium text-lg">{basicInfo.name || 'Unnamed Rodeo'}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-muted-foreground">
            {(basicInfo.start_date || basicInfo.end_date) && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {basicInfo.start_date || '?'} — {basicInfo.end_date || '?'}
                </span>
              </div>
            )}
            {basicInfo.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{basicInfo.location}</span>
              </div>
            )}
            {basicInfo.association && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>{basicInfo.association}</span>
              </div>
            )}
            {basicInfo.allow_cash_payment && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                <span>Cash payment allowed</span>
              </div>
            )}
            {/* Pay Later ships on, so only call out the non-default (off). */}
            {!basicInfo.allow_pay_later && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                <span>Pay Later disabled</span>
              </div>
            )}
            {/* Finals is the non-default, so it only earns a line when set. */}
            {basicInfo.is_finals && (
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                <span>Finals rodeo — everyone runs in every performance</span>
              </div>
            )}
            {/* Only shown when the rodeo overrides its organization; null
                (inherit) is the default and needs no line. */}
            {basicInfo.member_fee_enabled !== null && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>
                  {basicInfo.member_fee_enabled
                    ? 'Membership required (overrides organization)'
                    : 'Membership not required (overrides organization)'}
                </span>
              </div>
            )}
          </div>
          {basicInfo.description && (
            <p className="text-muted-foreground whitespace-pre-wrap pt-1 border-t mt-2">
              {basicInfo.description}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Performances summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Performances ({performances.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {performances.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">None configured</p>
          ) : (
            <div className="space-y-1">
              {performances
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((p) => (
                  <div
                    key={p._key}
                    className="flex items-center gap-2 text-sm flex-wrap"
                  >
                    <span className="font-medium">{p.performance_name}</span>
                    {p.is_slack && (
                      <Badge variant="outline" className="text-xs">Slack</Badge>
                    )}
                    <span className="text-muted-foreground">
                      {formatDate(p.performance_date) || ''}
                      {p.start_time ? ` · ${formatTime(p.start_time)}` : ''}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event configs summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Events ({events.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">None configured</p>
          ) : (
            <div className="space-y-3">
              {events.map((ec) => (
                <div key={ec._key} className="border-b last:border-b-0 pb-2 last:pb-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{ec.event_type}</span>
                    <Badge
                      variant="outline"
                      className={`text-xs ${ec.event_category === 'scored' ? 'text-amber-600' : 'text-blue-600'}`}
                    >
                      {ec.event_category}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {ec.entry_fee != null ? `$${ec.entry_fee} entry` : 'No entry fee'}
                    {ec.transaction_fee != null ? ` + $${ec.transaction_fee} txn` : ''}
                    {typeof ec.contestant_limit === 'number' && ec.contestant_limit > 0
                      ? ` · Max ${ec.contestant_limit}`
                      : ''}
                    {typeof ec.contestants_per_performance === 'number' &&
                    ec.contestants_per_performance > 0
                      ? ` · ${ec.contestants_per_performance}/perf`
                      : ''}
                    {ec.performance_assignments.length > 0 && (
                      <>
                        {' · Perfs: '}
                        {ec.performance_assignments
                          .map((a) => {
                            const name = perfName(a.performance_key);
                            return a.max_contestants != null
                              ? `${name} (cap ${a.max_contestants})`
                              : name;
                          })
                          .join(', ')}
                      </>
                    )}
                  </p>
                  {ec.round_configs.length > 0 && (
                    <div className="mt-1 ml-3 space-y-0.5">
                      {ec.round_configs
                        .sort((a, b) => a.round_number - b.round_number)
                        .map((r) => (
                          <p key={r._key} className="text-xs text-muted-foreground">
                            Round {r.round_number}: {r.round_name}
                            {r.is_short_round ? ' (Short Round)' : ''}
                            {r.advancing_count != null ? ` \u2014 Top ${r.advancing_count}` : ''}
                            {/* Which perf this round runs in — unassigned
                                rounds (incl. the Avg row) stay quiet. */}
                            {r.performance_key
                              ? ` \u00b7 ${perfName(r.performance_key)}`
                              : ''}
                          </p>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
